import {
  fromQuery,
  mapAsync,
  type Async,
  type QueryLike,
} from "../../-data/async";
import { buildPlansModel, type PlansModel } from "../../-data/plans-model";
import { toRow, type TaskRow } from "../../-data/task-model";
import type { PlansRead } from "../../-data/use-plans";
import type { WirePagination } from "../../-data/wire";
import type { TaskPage } from "../../-data/types";

/**
 * The Task plans tab, as state rather than as a pile of booleans.
 *
 * Two reads, nested one inside the other: the tab's own read of the plan cards,
 * and - only for a card someone has opened - that plan's tasks. The nesting is
 * the point. A card that is not expanded has no task state at all, not an empty
 * one; a card that is expanded is loading, failed, or holding a page. Writing
 * that as `isExpanded && !isLoading && tasks?.length` is how a closed card comes
 * to own a stale loading flag.
 *
 * Everything here is pure. The hooks stay in the components (they have to -
 * one `usePlanTasks` per open card, and hooks cannot be called conditionally);
 * this file is only the shape those hooks' results collapse into.
 */

/** Whether the page on screen is the whole list. */
export type PagingState =
  { kind: "complete" } | { kind: "has-more"; nextCursor: string };

/** One page of a plan's tasks, ready to draw. */
export interface PlanTasks {
  items: TaskRow[];
  paging: PagingState;
  /** What the service says the plan holds in total, when it says. */
  totalCount: number | null;
}

export type PlanTasksState = Async<PlanTasks>;

/**
 * A card, as the two states it can be in.
 *
 * `not-expanded` carries nothing, which is the whole reason to model it: the
 * tasks of a closed card are not "empty", they are unasked-for, and the type
 * that cannot tell those apart is the one that renders "no tasks" under a plan
 * holding four hundred.
 */
export type PlanCardState =
  { kind: "not-expanded" } | { kind: "expanded"; tasks: PlanTasksState };

/**
 * The tab's page: the cards, and the envelope that page came in.
 *
 * The envelope sits beside the model rather than inside it, because
 * `buildPlansModel` answers "what is each of these plans" and paging answers
 * "which plans are these" - one is derived from the rows, the other is a fact
 * about the request that fetched them, and a model that carried both would be
 * rebuilt on every page walk for no reason.
 */
export interface PlansPage {
  model: PlansModel;
  /** Straight through, because the pager reads all of it. */
  pagination: WirePagination;
}

export type PlansPageState = Async<PlansPage>;

function pagingOf(page: TaskPage): PagingState {
  return page.nextCursor === null
    ? { kind: "complete" }
    : { kind: "has-more", nextCursor: page.nextCursor };
}

/**
 * A plan's page, as rows.
 *
 * Nothing is narrowed here. The filter bar is part of the request an open card
 * makes (see `activationsInit` in `use-plans.ts`), so the page handed in is
 * already the answer - re-checking it was a filter over one truncated request's
 * worth of a plan rather than over the plan.
 */
export function toPlanTasks(page: TaskPage): PlanTasks {
  return {
    items: page.tasks.map(toRow),
    paging: pagingOf(page),
    totalCount: page.totalCount,
  };
}

/** One open card's task read, collapsed into its state. */
export function planTasksStateFrom(query: QueryLike<TaskPage>): PlanTasksState {
  return mapAsync(fromQuery(query), toPlanTasks);
}

/** The tab's own read, collapsed into its state. */
export function plansPageStateFrom(
  query: QueryLike<PlansRead>,
): PlansPageState {
  return mapAsync(fromQuery(query), (read) => ({
    model: buildPlansModel(read.plans),
    pagination: read.pagination,
  }));
}
